import type L from 'leaflet';
import type { MapStyle } from '../../store/uiStore';
import { MAX_ZOOM, MIN_ZOOM, MAX_NATIVE_TILE_ZOOM } from './types';

/**
 * Web basemap sources behind the map's style toggle.
 *
 * 'standard' is the illustrated topo treatment (how much color reaches the
 * screen is set by applyBasemapFilter, not by the tiles).
 * 'satellite' is photographic imagery plus a transparent roads/places layer
 * on top, i.e. Google's "hybrid" rather than bare imagery: an app built
 * around finding your own address is close to useless without street names.
 *
 * Native does not read this file. react-native-maps talks to the Google Maps
 * SDK directly, so Map.native.tsx just sets `mapType` and gets Google's own
 * imagery and labels for free.
 *
 * PROVIDER NOTE: Esri's imagery is here because it needs no API key, which
 * is what makes it right for a first look. Esri's terms for commercial use
 * of the public ArcGIS tile services are not clearly granted, so before the
 * prod cutover this should move to a keyed provider with unambiguous terms
 * (MapTiler's satellite tier is the cheapest fit). Swapping providers is a
 * change to this file plus the CSP entry in firebase.json and the cache rule
 * in workbox-config.js. Nothing else reads the tile URLs.
 */
interface BasemapSpec {
  url: string;
  attribution: string;
  /**
   * Last zoom the provider actually serves tiles for. Leaflet stretches this
   * level for deeper zooms rather than requesting tiles that 404 into grey.
   */
  maxNativeZoom: number;
  /**
   * Transparent tiles drawn over the base: roads, boundaries, place names.
   * Held as separate layers because applyBasemapFilter targets the base
   * layer's own container, so labels stay legible whatever the base does.
   *
   * Each carries the zoom band it actually earns its requests in. Stacking
   * three full-range layers tripled the request count against one host and
   * was the bulk of the zoom lag: 594 requests for ten zoom steps.
   */
  reference: ReferenceSpec[];
}

interface ReferenceSpec {
  url: string;
  /** Inclusive zoom band this layer is requested over. */
  minZoom?: number;
  maxZoom?: number;
}

const ESRI_TILES = 'https://server.arcgisonline.com/ArcGIS/rest/services';

// The two reference layers overlap by one zoom level so neither band ever
// hands over to a blank one.
const PLACE_LABEL_MAX_ZOOM = 14;
const ROAD_LABEL_MIN_ZOOM = 13;

// Esri's free tile service returns intermittent 502s under load. Leaflet
// never retries a failed tile, so one blip leaves a permanent grey square
// until the user pans away and back. Retry a couple of times, backing off,
// before giving up.
const TILE_RETRY_LIMIT = 2;
const TILE_RETRY_BASE_DELAY_MS = 400;

/**
 * Re-request tiles that failed, up to TILE_RETRY_LIMIT times each.
 *
 * Leaflet hands us the <img> that failed. Blanking src before restoring it
 * forces a fresh request rather than a replay of the failed response, and
 * the parentNode check drops retries for tiles Leaflet has already discarded
 * (panning away during the backoff would otherwise fire pointless requests).
 */
function attachTileRetry(layer: L.TileLayer): void {
  const attempts = new WeakMap<HTMLImageElement, number>();
  layer.on('tileerror', (e: L.TileErrorEvent) => {
    const img = e.tile as HTMLImageElement | undefined;
    if (!img) return;
    const tried = attempts.get(img) ?? 0;
    if (tried >= TILE_RETRY_LIMIT) return;
    attempts.set(img, tried + 1);
    const src = img.src;
    setTimeout(
      () => {
        if (!img.parentNode) return;
        img.src = '';
        img.src = src;
      },
      TILE_RETRY_BASE_DELAY_MS * (tried + 1)
    );
  });
}

const BASEMAPS: Record<MapStyle, BasemapSpec> = {
  standard: {
    // Esri World Topo, not tile.openstreetmap.org, since Nov 3 2026. An OSM
    // edit on Oct 29 deleted the forest across the San Bernardino range; it
    // was restored Nov 1, but osm.org only redraws a tile when someone views
    // it, so Big Bear stayed a checkerboard of green and blank tiles for
    // days. World Topo draws from Esri's own landcover, so one mapper's edit
    // can't blank it. Same terms caveat as satellite (PROVIDER NOTE above).
    url: `${ESRI_TILES}/World_Topo_Map/MapServer/tile/{z}/{y}/{x}`,
    // Short form on purpose, for the same reason as satellite below: Esri's
    // full credit ("Esri, HERE, Garmin, USGS, OpenStreetMap contributors...")
    // wraps to two lines at 390px and runs under the map key. Revisit with
    // the provider decision.
    attribution:
      'Map &copy; <a href="https://www.esri.com/">Esri</a>, <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    // Measured to 19 over Big Bear, the same ceiling OSM had.
    maxNativeZoom: MAX_NATIVE_TILE_ZOOM,
    reference: [],
  },
  satellite: {
    // Esri paths are {z}/{y}/{x}, not the {z}/{x}/{y} everything else uses.
    url: `${ESRI_TILES}/World_Imagery/MapServer/tile/{z}/{y}/{x}`,
    // Short form on purpose. Esri's published credit for this service is
    // "Esri, Maxar, Earthstar Geographics, USDA, USGS, AeroGRID, IGN, and the
    // GIS User Community"; at 390px wide that wraps to three lines and runs
    // under the map legend. Revisit alongside the provider decision above,
    // since the replacement provider sets its own attribution terms.
    attribution: 'Imagery &copy; <a href="https://www.esri.com/">Esri</a>, Maxar, USDA, USGS',
    // Esri publishes deeper levels over parts of the US, but coverage is
    // patchy and a missing tile reads as a grey hole. 19 is the level it
    // serves everywhere, and at roughly 0.3m per pixel it already resolves
    // individual roofs and trees, which is the whole point of this view.
    maxNativeZoom: 19,
    reference: [
      // Town and place names. Useful when you're looking at the whole lake,
      // redundant once street names are on screen.
      {
        url: `${ESRI_TILES}/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}`,
        maxZoom: PLACE_LABEL_MAX_ZOOM,
      },
      // Roads and street names: the layer that makes "find my address" work,
      // and worthless at lake-wide zoom where it draws bare highways.
      {
        url: `${ESRI_TILES}/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}`,
        minZoom: ROAD_LABEL_MIN_ZOOM,
      },
    ],
  },
};

/**
 * Build the Leaflet layers for a style. Callers add `base` first, then every
 * entry of `reference` in order: layers share the tile pane, so paint order
 * is DOM order. Both sit below the marker pane either way.
 */
export function createBasemapLayers(
  leaflet: typeof L,
  style: MapStyle
): { base: L.TileLayer; reference: L.TileLayer[] } {
  const spec = BASEMAPS[style];
  const base = leaflet.tileLayer(spec.url, {
    maxZoom: MAX_ZOOM,
    maxNativeZoom: spec.maxNativeZoom,
    attribution: spec.attribution,
  });
  attachTileRetry(base);

  const reference = spec.reference.map((ref) => {
    const layer = leaflet.tileLayer(ref.url, {
      // maxZoom/minZoom hide the layer outside its band. maxNativeZoom would
      // instead keep it on screen and stretch its last tile, which for a
      // label layer means blurry giant text over the imagery.
      maxZoom: ref.maxZoom ?? MAX_ZOOM,
      minZoom: ref.minZoom ?? MIN_ZOOM,
      maxNativeZoom: spec.maxNativeZoom,
      // Labels are supporting detail, so let them settle after the zoom
      // animation instead of fetching a full tile set at every intermediate
      // level the wheel passes through.
      updateWhenZooming: false,
    });
    attachTileRetry(layer);
    return layer;
  });

  return { base, reference };
}
